import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ESTADOS = [["pendiente", "Pendiente"], ["revisado", "Revisado"], ["resuelto", "Resuelto"]];
const COLOR = { pendiente: "bg-[#FEF3C7] text-[#92400E]", revisado: "bg-[#E0ECF8] text-[#1E4E79]", resuelto: "bg-[#E8F0EC] text-[#2E5645]" };

const ICONO = { fallo: "AlertCircle", idea: "Lightbulb", otro: "MessageCircle" };
const fecha = (iso) => new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const Captura = ({ id }) => {
  const [url, setUrl] = useState(null);
  const ver = async () => {
    const r = await api.get(`/admin/comentarios/${id}/captura`, { responseType: "blob" });
    setUrl(URL.createObjectURL(r.data));
  };
  if (url) return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="Captura adjunta" className="mt-3 max-h-72 rounded-xl border border-[#E5DFD5]" data-testid={`comentario-captura-img-${id}`} /></a>;
  return <button data-testid={`comentario-ver-captura-${id}`} onClick={ver} className="mt-3 min-h-[44px] px-4 rounded-xl bg-[#F2EDE4] text-[#1C2421] font-semibold text-sm inline-flex items-center gap-2"><Icons.Image size={18} /> Ver captura</button>;
};

const Estado = ({ c, onCambio }) => {
  const cambiar = async (estado) => {
    if (estado === c.estado) return;
    try { const r = await api.patch(`/admin/comentarios/${c.id}`, { estado }); onCambio({ ...c, ...r.data }); }
    catch { toast.error("No se pudo cambiar el estado."); }
  };
  return (
    <div className="mt-3">
      <div className="flex gap-2" role="radiogroup" aria-label="Estado">
        {ESTADOS.map(([k, l]) => (
          <button key={k} role="radio" aria-checked={c.estado === k} data-testid={`comentario-estado-${k}-${c.id}`} onClick={() => cambiar(k)}
            className={`flex-1 min-h-[40px] rounded-xl text-sm font-semibold transition-colors ${c.estado === k ? COLOR[k] + " ring-2 ring-current" : "bg-[#F2EDE4] text-[#4A5550]"}`}>{l}</button>
        ))}
      </div>
      {c.estado_fecha && <p className="text-xs text-[#9AA39D] mt-1.5" data-testid={`comentario-estado-fecha-${c.id}`}>Último cambio de estado: {fecha(c.estado_fecha)}</p>}
    </div>
  );
};

const Nota = ({ c, onCambio }) => {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(c.nota);
  const [busy, setBusy] = useState(false);
  const guardar = async () => {
    setBusy(true);
    try { const r = await api.patch(`/admin/comentarios/${c.id}`, { nota: texto }); onCambio({ ...c, ...r.data }); setEditando(false); }
    catch { toast.error("No se pudo guardar la nota."); }
    finally { setBusy(false); }
  };
  if (editando) return (
    <div className="mt-3">
      <textarea data-testid={`comentario-nota-input-${c.id}`} value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} maxLength={2000} autoFocus
        placeholder="Nota privada (solo la ves tú)" className="w-full rounded-xl border border-[#E5DFD5] p-3 text-base focus:outline-none focus:border-[#3A6B56]" />
      <div className="flex gap-2 mt-2">
        <button data-testid={`comentario-nota-guardar-${c.id}`} disabled={busy} onClick={guardar} className="min-h-[40px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold text-sm disabled:opacity-60">Guardar nota</button>
        <button data-testid={`comentario-nota-cancelar-${c.id}`} onClick={() => { setTexto(c.nota); setEditando(false); }} className="min-h-[40px] px-4 rounded-xl text-[#4A5550] font-semibold text-sm">Cancelar</button>
      </div>
    </div>
  );
  return c.nota ? (
    <div className="mt-3 bg-[#FAF8F5] border border-dashed border-[#D6CFC2] rounded-xl p-3" data-testid={`comentario-nota-${c.id}`}>
      <p className="text-xs font-bold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1"><Icons.Lock size={12} /> Nota interna</p>
      <p className="text-base text-[#1C2421] mt-1 whitespace-pre-wrap">{c.nota}</p>
      <div className="flex items-center mt-1">
        {c.nota_fecha && <span className="text-xs text-[#9AA39D]">Modificada {fecha(c.nota_fecha)}</span>}
        <button data-testid={`comentario-nota-editar-${c.id}`} onClick={() => setEditando(true)} className="ml-auto text-sm font-semibold text-[#3A6B56] min-h-[36px] px-2">Editar</button>
      </div>
    </div>
  ) : (
    <button data-testid={`comentario-nota-anadir-${c.id}`} onClick={() => setEditando(true)} className="mt-3 text-sm font-semibold text-[#3A6B56] inline-flex items-center gap-1 min-h-[36px]"><Icons.StickyNote size={16} /> Añadir nota interna</button>
  );
};

const Comentario = ({ c, onCambio }) => {
  const Icon = Icons[ICONO[c.tipo]] || Icons.MessageCircle;
  return (
    <li className="py-4 border-b border-[#F2EDE4] last:border-0" data-testid={`comentario-${c.id}`}>
      <div className="flex items-center gap-2 text-sm">
        <Icon size={18} className={c.tipo === "fallo" ? "text-[#B91C1C]" : "text-[#3A6B56]"} />
        <span className="font-bold text-[#1C2421]">{c.tipo_label}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${COLOR[c.estado]}`} data-testid={`comentario-estado-badge-${c.id}`}>{ESTADOS.find(([k]) => k === c.estado)?.[1]}</span>
        <span className="text-[#6E7A74] ml-auto">Recibido {fecha(c.created_at)}</span>
      </div>
      <p className="text-base text-[#1C2421] mt-2 whitespace-pre-wrap">{c.mensaje}</p>
      <p className="text-sm text-[#6E7A74] mt-2">{c.anonimizado ? "Usuario que eliminó su cuenta (anónimo)" : <>{c.nombre || c.email}{c.nombre && c.email ? ` · ${c.email}` : ""}</>}</p>
      {c.pantalla && <p className="text-sm text-[#6E7A74]">Pantalla: {c.pantalla}</p>}
      {c.dispositivo && <p className="text-xs text-[#9AA39D] mt-1 break-words">{c.dispositivo}</p>}
      {c.tiene_captura && <Captura id={c.id} />}
      <Estado c={c} onCambio={onCambio} />
      <Nota c={c} onCambio={onCambio} />
    </li>
  );
};

// Perfil › Comentarios de la beta (solo administrador).
export const ComentariosBeta = () => {
  const [lista, setLista] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const actualizar = (n) => setLista((l) => l.map((c) => (c.id === n.id ? n : c)));
  const visibles = (lista || []).filter((c) => filtro === "todos" || c.estado === filtro);
  const cuenta = (k) => (lista || []).filter((c) => c.estado === k).length;
  useEffect(() => { api.get("/admin/comentarios").then((r) => setLista(r.data)).catch(() => setLista([])); }, []);
  const pendientes = cuenta("pendiente");
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm" data-testid="comentarios-beta">
      <button data-testid="comentarios-beta-toggle" onClick={() => setAbierto((v) => !v)} className="w-full flex items-center gap-2 text-left">
        <Icons.Inbox size={20} className="text-[#3A6B56]" />
        <span className="flex-1 font-heading text-lg font-bold text-[#1C2421]">Comentarios de la beta
          {pendientes > 0 && <span className="font-sans text-base font-semibold text-[#92400E]" data-testid="comentarios-beta-pendientes"> · {pendientes} {pendientes === 1 ? "pendiente" : "pendientes"}</span>}</span>
        {abierto ? <Icons.ChevronUp size={20} className="text-[#9AA39D]" /> : <Icons.ChevronDown size={20} className="text-[#9AA39D]" />}
      </button>
      {abierto && (lista === null ? <p className="text-base text-[#6E7A74] mt-3">Cargando…</p>
        : lista.length === 0 ? <p className="text-base text-[#6E7A74] mt-3" data-testid="comentarios-beta-vacio">Todavía no hay comentarios.</p>
        : <>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="comentarios-beta-filtros">
            {[["todos", `Todos (${lista.length})`], ...ESTADOS.map(([k, l]) => [k, `${l} (${cuenta(k)})`])].map(([k, l]) => (
              <button key={k} data-testid={`comentarios-filtro-${k}`} onClick={() => setFiltro(k)}
                className={`min-h-[40px] px-3 rounded-full text-sm font-semibold ${filtro === k ? "bg-[#3A6B56] text-white" : "bg-[#F2EDE4] text-[#1C2421]"}`}>{l}</button>
            ))}
          </div>
          {visibles.length === 0 ? <p className="text-base text-[#6E7A74] mt-3" data-testid="comentarios-beta-filtro-vacio">No hay comentarios con este estado.</p>
            : <ul className="mt-2" data-testid="comentarios-beta-lista">{visibles.map((c) => <Comentario key={c.id} c={c} onCambio={actualizar} />)}</ul>}
        </>)}
    </div>
  );
};
